(() => {
  if (customElements.get('shop-the-collection')) return;

  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const DESKTOP_MQ = '(min-width: 1000px)';

  class ShopTheCollection extends HTMLElement {
    connectedCallback() {
      this.totalEl = this.querySelector('.shop-the-collection__total');
      this.totalPriceEl = this.querySelector('[data-total-price]');
      this.atcButton = this.querySelector('.shop-the-collection__atc');
      this.atcWrap = this.querySelector('.shop-the-collection__atc-wrap');
      this.atcAnchor = this.querySelector('.shop-the-collection__atc-anchor');
      this.labelEmpty = this.dataset.labelEmpty || '';
      this.labelOne = this.dataset.labelOne || '';
      this.labelOther = this.dataset.labelOther || '';
      this.currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';
      this.moneyFormatter = new Intl.NumberFormat(window.Shopify?.locale || undefined, {
        style: 'currency',
        currency: this.currency
      });

      this.hasSelection = false;
      this.featureInView = false;
      this.isSticky = false;
      this.isAdding = false;
      this.desktopMq = window.matchMedia(DESKTOP_MQ);

      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onAtcClick = this.onAtcClick.bind(this);
      this.onReposition = this.onReposition.bind(this);
      this.onDesktopChange = this.onDesktopChange.bind(this);

      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.addEventListener('input', this.onChange);
      this.atcButton?.addEventListener('click', this.onAtcClick);
      this.desktopMq.addEventListener?.('change', this.onDesktopChange);
      this.desktopMq.addListener?.(this.onDesktopChange);

      this.setupObservers();
      this.updateUI();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('input', this.onChange);
      this.atcButton?.removeEventListener('click', this.onAtcClick);
      this.desktopMq.removeEventListener?.('change', this.onDesktopChange);
      this.desktopMq.removeListener?.(this.onDesktopChange);
      this.teardownStickyListeners();
      this.featureObserver?.disconnect();
      if (this._raf) cancelAnimationFrame(this._raf);
      this.clearFixedPosition();
    }

    get isDesktop() {
      return this.desktopMq.matches;
    }

    setupObservers() {
      if (!('IntersectionObserver' in window)) {
        this.featureInView = true;
        return;
      }

      this.featureObserver = new IntersectionObserver(
        ([entry]) => {
          this.featureInView = entry.isIntersecting;
          this.syncSticky();
        },
        { threshold: 0 }
      );
      this.featureObserver.observe(this);
    }

    onDesktopChange() {
      this.syncSticky();
    }

    onReposition() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        this.syncSticky();
      });
    }

    setupStickyListeners() {
      if (this._stickyListenersBound) return;
      this._stickyListenersBound = true;
      window.addEventListener('scroll', this.onReposition, { passive: true });
      window.addEventListener('resize', this.onReposition, { passive: true });
    }

    teardownStickyListeners() {
      if (!this._stickyListenersBound) return;
      this._stickyListenersBound = false;
      window.removeEventListener('scroll', this.onReposition);
      window.removeEventListener('resize', this.onReposition);
    }

    getAtcHeight() {
      if (!this.atcWrap) return 0;
      if (this.isSticky && this.atcAnchor?.style.minHeight) {
        return parseFloat(this.atcAnchor.style.minHeight) || this.atcWrap.offsetHeight;
      }
      return this.atcWrap.offsetHeight;
    }

    hasReachedNaturalPosition() {
      if (!this.atcAnchor || !this.atcWrap) return true;
      const height = this.getAtcHeight() || 1;
      return this.atcAnchor.getBoundingClientRect().top <= window.innerHeight - height + 0.5;
    }

    applyFixedPosition({ animate = false } = {}) {
      if (!this.atcWrap || !this.atcAnchor) return;

      const height = this.atcWrap.offsetHeight;
      const featureRect = this.getBoundingClientRect();
      const wasFixed = this.atcWrap.classList.contains('is-fixed');

      this.atcAnchor.style.minHeight = `${height}px`;
      this.atcWrap.classList.add('is-fixed');
      this.atcWrap.style.cssText = `position:fixed;left:${Math.round(featureRect.left)}px;width:${Math.round(featureRect.width)}px;right:auto;bottom:0;z-index:5;`;

      if (animate && !wasFixed) {
        this.atcWrap.classList.remove('is-animating');
        void this.atcWrap.offsetWidth;
        this.atcWrap.classList.add('is-animating');
      }
    }

    clearFixedPosition() {
      if (!this.atcWrap) return;

      this.atcWrap.classList.remove('is-fixed', 'is-animating');
      this.atcWrap.style.cssText = '';

      if (this.atcAnchor) this.atcAnchor.style.minHeight = '';
    }

    syncSticky() {
      const canStick =
        this.isDesktop &&
        this.hasSelection &&
        this.featureInView &&
        !this.hasReachedNaturalPosition();

      if (canStick) {
        this.applyFixedPosition({ animate: !this.isSticky });
        this.isSticky = true;
        this.classList.add('is-sticky-active');
        this.setupStickyListeners();
        return;
      }

      this.clearFixedPosition();
      this.isSticky = false;
      this.classList.remove('is-sticky-active');

      if (this.isDesktop && this.hasSelection) {
        this.setupStickyListeners();
      } else {
        this.teardownStickyListeners();
      }
    }

    onClick(event) {
      const qtyBtn = event.target.closest('[data-qty-action]');
      if (!qtyBtn) return;

      const row = qtyBtn.closest('.shop-the-collection__row');
      const input = row?.querySelector('.shop-the-collection__qty-input');
      if (!input) return;

      const current = parseInt(input.value, 10) || 1;
      input.value = String(
        qtyBtn.dataset.qtyAction === 'increase' ? current + 1 : Math.max(1, current - 1)
      );
      this.updateUI();
    }

    onChange(event) {
      const row = event.target.closest('.shop-the-collection__row');
      if (!row) {
        this.updateUI();
        return;
      }

      if (event.target.classList.contains('shop-the-collection__checkbox')) {
        row.classList.toggle('is-selected', event.target.checked);
        const qty = row.querySelector('.shop-the-collection__qty');
        if (qty) qty.hidden = !event.target.checked;
      }

      if (event.target.classList.contains('shop-the-collection__variant-select')) {
        const option = event.target.selectedOptions?.[0] || event.target;
        const price = option.dataset?.price || event.target.dataset.price;
        const priceEl = row.querySelector('[data-price]');
        if (priceEl && price != null) {
          priceEl.textContent = this.formatMoney(parseInt(price, 10));
        }
        row.dataset.selectedVariantId = event.target.value;
      }

      this.updateUI();
    }

    getUnitPrice(select) {
      if (select?.tagName === 'SELECT') {
        return parseInt(select.selectedOptions[0]?.dataset.price || '0', 10);
      }
      if (select?.dataset?.price) {
        return parseInt(select.dataset.price, 10);
      }
      return 0;
    }

    getSelectedItems() {
      const items = [];
      this.querySelectorAll('.shop-the-collection__row').forEach((row) => {
        const checkbox = row.querySelector('.shop-the-collection__checkbox');
        if (!checkbox?.checked || checkbox.disabled) return;

        const select = row.querySelector('.shop-the-collection__variant-select');
        const qtyInput = row.querySelector('.shop-the-collection__qty-input');
        const variantId = parseInt(select?.value || row.dataset.selectedVariantId, 10);
        const quantity = Math.max(1, parseInt(qtyInput?.value, 10) || 1);
        const unitPrice = this.getUnitPrice(select);

        if (variantId) items.push({ id: variantId, quantity, unitPrice });
      });
      return items;
    }

    formatMoney(cents) {
      return this.moneyFormatter.format((cents || 0) / 100);
    }

    buildLabel(count, priceText) {
      if (count < 1) return this.labelEmpty;
      return (count === 1 ? this.labelOne : this.labelOther)
        .replace(/\[count\]/g, String(count))
        .replace(/\[price\]/g, priceText);
    }

    updateUI() {
      const items = this.getSelectedItems();
      const count = items.reduce((sum, item) => sum + item.quantity, 0);
      const total = items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
      const priceText = this.formatMoney(total);
      this.hasSelection = count > 0;

      if (this.totalEl) this.totalEl.hidden = !this.hasSelection;
      if (this.totalPriceEl) this.totalPriceEl.textContent = priceText;

      if (this.atcButton) {
        this.atcButton.disabled = !this.hasSelection || this.isAdding;
        this.atcButton.textContent = this.buildLabel(count, priceText);
      }

      this.syncSticky();
    }

    async onAtcClick() {
      if (this.isAdding) return;

      const items = this.getSelectedItems().map(({ id, quantity }) => ({ id, quantity }));
      if (!items.length || this.atcButton?.disabled) return;

      this.isAdding = true;
      this.atcButton.classList.add('is-loading');
      this.atcButton.disabled = true;
      this.atcButton.setAttribute('aria-busy', 'true');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        })
      );

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items,
            sections: sectionsToBundle.join(','),
            sections_url: window.location.pathname
          })
        });

        const responseJson = await response.json();
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

        if (!response.ok) {
          const error = responseJson.description || responseJson.message || 'Add to cart failed';
          this.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error } }));
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          throw new Error(error);
        }

        if (
          window.themeVariables?.settings?.cartType === 'page' ||
          window.themeVariables?.settings?.pageType === 'cart'
        ) {
          window.location.href = `${root}cart`;
          return;
        }

        const cartContent = await (await fetch(`${root}cart.js`)).json();
        cartContent.sections = responseJson.sections;
        const addedItems = responseJson.items || [responseJson];

        this.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            detail: {
              items: addedItems,
              cart: cartContent,
              onSuccessDo: 'force_open_drawer'
            }
          })
        );

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              onSuccessDo: 'force_open_drawer',
              cart: cartContent
            }
          })
        );
      } catch (error) {
        console.error(error);
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      } finally {
        this.isAdding = false;
        this.atcButton.classList.remove('is-loading');
        this.atcButton.removeAttribute('aria-busy');
        this.updateUI();
      }
    }
  }

  customElements.define('shop-the-collection', ShopTheCollection);
})();
